import { createContext, useContext, ReactNode, useCallback, useMemo } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/lib/convex';
import { useAuth } from '@/contexts/hooks/useAuth';
import type { Notification, NotificationStats } from '@/types/notification';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  stats: NotificationStats | null;
  loading: boolean;
  markAsRead: (notificationId: string) => Promise<boolean>;
  markAllAsRead: () => Promise<boolean>;
  deleteNotification: (notificationId: string) => Promise<boolean>;
  refreshNotifications: () => Promise<void>;
  clearOldNotifications: (daysToKeep?: number) => Promise<number>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export { NotificationContext };

export function useNotificationContext() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within NotificationProvider');
  }
  return context;
}

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const { user } = useAuth();

  // Convex reactive queries - auto-update when data changes
  const unreadCount = useQuery(
    api.notifications.getUnreadCount,
    user ? {} : "skip"
  ) ?? 0;

  const notificationsResult = useQuery(
    api.notifications.list,
    user ? { paginationOpts: { numItems: 50, cursor: null } } : "skip"
  );

  const markAsReadMutation = useMutation(api.notifications.markAsRead);
  const markAllAsReadMutation = useMutation(api.notifications.markAllAsRead);
  const deleteNotificationMutation = useMutation(api.notifications.deleteNotification);

  // Map Convex notifications to the existing Notification type
  const notifications: Notification[] = useMemo(() => {
    if (!notificationsResult?.page) return [];
    return notificationsResult.page.map((n) => ({
      id: n._id,
      user_id: n.userId,
      type: n.type as Notification['type'],
      category: (n.category ?? 'system') as Notification['category'],
      title: n.title ?? '',
      message: n.message ?? undefined,
      data: n.data as Record<string, unknown> | undefined,
      read: n.isRead ?? false,
      action_url: n.actionUrl ?? undefined,
      related_user_id: n.senderId ?? undefined,
      created_at: new Date(n._creationTime).toISOString(),
      updated_at: new Date(n._creationTime).toISOString(),
    }));
  }, [notificationsResult]);

  const loading = user ? notificationsResult === undefined : false;

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await markAsReadMutation({ id: notificationId as any });
      return true;
    } catch {
      return false;
    }
  }, [markAsReadMutation]);

  const markAllAsRead = useCallback(async () => {
    try {
      await markAllAsReadMutation({});
      return true;
    } catch {
      return false;
    }
  }, [markAllAsReadMutation]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      await deleteNotificationMutation({ id: notificationId as any });
      return true;
    } catch {
      return false;
    }
  }, [deleteNotificationMutation]);

  const refreshNotifications = useCallback(async () => {
    // Convex useQuery auto-refreshes - no manual refresh needed
  }, []);

  const clearOldNotifications = useCallback(async (_daysToKeep?: number) => {
    // Will be implemented as a server-side cleanup action
    return 0;
  }, []);

  const contextValue = useMemo<NotificationContextType>(() => ({
    notifications,
    unreadCount,
    stats: null,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications,
    clearOldNotifications,
  }), [
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications,
    clearOldNotifications,
  ]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
}
